import React from 'react';
import { ILayer } from '../../../models';
import {
    PrimaryButton, DefaultButton, Panel, PanelType, Label, DatePicker,
    DayOfWeek, Dropdown, IDropdownOption, Stack, TextField, MessageBar,
    MessageBarType, IconButton, Link, Dialog, DialogFooter, TagPicker, ITag,
    Icon, Text, Toggle
} from '@fluentui/react';
import { IAssignmentDTO, ICreateOrUpdateSupplierDTO, IMapData, IRegionDTO, ICertificationDTO } from '../../../models';
import styles from './AssignmentPanel.module.scss';
import { Services, IUserService } from '../../../services';
import App from '../../../App';
import { VisibilityControl } from '../../VisibilityControl/VisibilityControl';
import Utilities, { BackgroundMaps, MapLayers, Status } from '../../../helpers/Utilities';
import GermanLeafletMap from '../Maps/GermanLeafletMap';
import GermanDocumentationDialog from '../Documentation/GermanDocumentationDialog';
import { IAssignmentFileDTO } from '../../../models/DTO/IAssignmentFileDTO';
import { TooltipComponent } from '../../../helpers/TooltipComponent';
import { AssignmentServiceProvider, IAssignmentService } from '../../../services/assignmentService';
import { UserServiceProvider } from '../../../services/userService';
import { isMobileOrTablet } from '../../../components/VisibilityControl/UserAgentControl';
import L, { LatLngTuple } from 'leaflet';
import { withLanguage, WithLanguage } from '../../../LanguageContext';
import { GermanAssignmentHandler, IAssignmentCrudHandler } from './GermanAssignmentHandler';
import { germanRegionNos } from '../../../constants/StaticVariables';


//#region Interfaces
interface IGermanAssignmentPanelProps extends WithLanguage {
    selectedAssignment?: IAssignmentDTO;
    onDismiss(): void;
    onChanged(assignment: IAssignmentDTO, showAssignmentPanel: boolean): void;
    allowSelectionOfSupplier: boolean;
}

interface IGermanAssignmentPanelState {
    step: CreationSteps;
    mode: FormMode;
    currentItem: IAssignmentDTO;
    stackSumFromMap: number;
    stackTreeSpecices: string;
    suppliers: ICreateOrUpdateSupplierDTO[];
    regions: IRegionDTO[];
    certifications?: ICertificationDTO[];
    isSaving: boolean;
    showValidationBox: boolean;
    validationMessage: string;
    assignmentSavedAfterInactivity: boolean;
    isOffline?: boolean;
    showLeaveConfirmDialog: boolean;
    userOverrideEstimatedAmount: boolean;
    showDocumentationOutdatedWarning: boolean;
    mapRef?: any;
    layers: ILayer[];
    wfsFeatures: { [layerName: string]: any };
}

enum CreationSteps {
    Assignment,
    Map,
    Documentation
}

export enum FormMode {
    New,
    Edit,
    ReadOnly
}
//#endregion

class GermanAssignmentPanel extends React.Component<IGermanAssignmentPanelProps, IGermanAssignmentPanelState> {
    private assignmentService: IAssignmentService;
    private userService: IUserService;
    private assignmentCrudHandler: IAssignmentCrudHandler;
    private fileInput_OwnMap;
    private hasUnsavedChanges: boolean = false;
    private readonly germanBackgroundLayers: ILayer[] = [
        BackgroundMaps.DTK25Germany,
        BackgroundMaps.Luftbild,
    ];
    private readonly germanOverlayLayers: ILayer[] = [
        { ...MapLayers.FaunaFloraHabitatDE },
        { ...MapLayers.VogelschutzgebieteDE },
        { ...MapLayers.Wendemoeglichkeiten },
        { ...MapLayers.Forstwege },
        { ...MapLayers.Rettungspunkte }
    ];

    //#region Constructor
    constructor(props: IGermanAssignmentPanelProps) {
        super(props);


        this.assignmentService = Services.AssignmentService.Initialize(new AssignmentServiceProvider());
        this.userService = Services.UserService.Initialize(new UserServiceProvider());
        this.assignmentCrudHandler = new GermanAssignmentHandler();

        // Set form mode
        let mode = props.allowSelectionOfSupplier ? FormMode.New :
            props.selectedAssignment ?
                (props.selectedAssignment.status == Status.WaitingForApproval ||
                    props.selectedAssignment.status == Status.Approved ||
                    props.selectedAssignment.status == Status.Rejected ||
                    props.selectedAssignment.status == Status.Completed) ? FormMode.ReadOnly : FormMode.Edit
                : FormMode.New;

        if (props.selectedAssignment && (App.IsUserInRole("DL") || App.IsUserInRole("Purchaser"))) {
            if (props.selectedAssignment.status == Status.Offline) {
                mode = FormMode.New;
            } else {
                mode = (props.selectedAssignment.status == Status.Approved ||
                    props.selectedAssignment.status == Status.Completed) ? FormMode.ReadOnly : FormMode.Edit;
            }
        }

        this.state = {
            step: CreationSteps.Assignment,
            mode: mode,
            currentItem: props.selectedAssignment ? props.selectedAssignment : {
                created: new Date(),
                status: Status.Open,
                hdaoNo: undefined,
                files: [],
                typeId: "Flis",
                biomassCategory: undefined,
                ressourceType: undefined,
                trappingDate: undefined
            },
            stackSumFromMap: 0,
            stackTreeSpecices: "",
            suppliers: [] as ICreateOrUpdateSupplierDTO[],
            regions: [] as IRegionDTO[],
            isSaving: false,
            showValidationBox: false,
            validationMessage: "",
            assignmentSavedAfterInactivity: false,
            isOffline: !navigator.onLine,
            showLeaveConfirmDialog: false,
            userOverrideEstimatedAmount: false,
            showDocumentationOutdatedWarning: false,
            mapRef: undefined,
            layers: [],
            wfsFeatures: {}
        }

        this.load();
    }
    //#endregion

    //#region Lifecycle
    componentDidMount() {
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        window.addEventListener('popstate', this.handlePopState);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        window.removeEventListener('popstate', this.handlePopState);
    }
    //#endregion

    //#region Methods
    private async load() {
        if (this.props.allowSelectionOfSupplier ||
            (this.state.currentItem.status === Status.Offline && !this.state.isOffline)) {
            let suppliers: ICreateOrUpdateSupplierDTO[] = await this.userService.GetSuppliers();
            this.setState({ suppliers: suppliers });
        }

        if (App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance")) {
            // Load regions and restrict selection to the two designated German region numbers
            let allRegions: IRegionDTO[] = await this.userService.GetRegions();

            let germanRegions = allRegions.filter(region =>
                germanRegionNos.some(regionNo => region.name.startsWith(regionNo.toString()) || region.id === regionNo.toString()));

            // Ensure the already assigned region is still selectable even if it's no longer in the designated list
            let currentRegionID = this.state.currentItem.regionID;
            if (currentRegionID && !germanRegions.some(region => region.id === currentRegionID)) {
                let existingRegion = allRegions.find(region => region.id === currentRegionID);
                if (existingRegion) {
                    germanRegions = [...germanRegions, existingRegion];
                }
            }

            this.setState({ regions: germanRegions });
        }

        // Calculate stack sum from MapData if existing item
        if (this.props.selectedAssignment && this.state.currentItem.mapData) {
            let mapData: IMapData = JSON.parse(this.state.currentItem.mapData) as IMapData;
            let treeSpecices = this.getTreeSpecicesUsedInStacks(mapData);
            this.setState({ stackSumFromMap: mapData.stackSum, stackTreeSpecices: treeSpecices });
        }

        if (this.props.selectedAssignment) {
            let files = await this.assignmentService.GetFilesForAssignment(this.props.selectedAssignment);
            if (files.length > 0) {
                this.setState({ currentItem: { ...this.state.currentItem, files: files } });
            }
        }

        this.hasMapBeenChangedAfterDocumentationProcess();
    }

    private async preSaveAssignment() {
        let newId = await this.assignmentService.CreateAssignment(this.state.currentItem);

        if (newId != "") {
            this.updateProperty({ id: newId })
        } else {
            alert(this.props.t('assignmentPanel.createError'));
        }
    }

    private onMapSaved(mapData: IMapData, canEdit: boolean, mapRef?: any, layers?: any[], wfsFeatures?: { [layerName: string]: any }): void {
        if (!mapData.fixedTaskPosition && mapData.workingAreas && mapData.workingAreas.length > 0 &&
            mapData.workingAreas[0].coordinates && mapData.workingAreas[0].coordinates.length > 0) {
            mapData.defaultPosition = [
                mapData.workingAreas[0].coordinates[0].lat,
                mapData.workingAreas[0].coordinates[0].lng
            ] as LatLngTuple;
        }

        let treeSpecices = this.getTreeSpecicesUsedInStacks(mapData);
        let timestamp = canEdit ? new Date() : this.state.currentItem.mapTimestamp;

        const stackSumChanged = this.state.stackSumFromMap !== mapData.stackSum;

        let newEstimatedAmount: number | undefined;
        if (stackSumChanged) {
            newEstimatedAmount = mapData.stackSum > 0 ? mapData.stackSum : this.state.currentItem.estimatedAmount;
        } else {
            newEstimatedAmount = this.state.currentItem.estimatedAmount;
        }

        this.setState({
            stackSumFromMap: mapData.stackSum,
            stackTreeSpecices: treeSpecices,
            currentItem: {
                ...this.state.currentItem,
                mapData: JSON.stringify(mapData),
                estimatedAmount: newEstimatedAmount,
                mapTimestamp: timestamp
            },
            step: CreationSteps.Assignment,
            userOverrideEstimatedAmount: stackSumChanged ? false : this.state.userOverrideEstimatedAmount,
            mapRef: mapRef || this.state.mapRef,
            layers: layers || this.state.layers,
            wfsFeatures: wfsFeatures || this.state.wfsFeatures
        }, () => {
            // Check if documentation is outdated after map changes
            this.hasMapBeenChangedAfterDocumentationProcess();
        });
    }

    private validateForm_CanApprove(): boolean {
        const validation = this.assignmentCrudHandler.validateGermanAssignment(this.state.currentItem);
        const { t } = this.props;

        if (!validation.isValid) {
            // Translate the validation keys
            const translatedFields = validation.missingFields.map(field => {
                return field
                    .replace('{{regionsNummerLabel}}', t('assignmentPanel.regionsNummerLabel'))
                    .replace('{{validationTaskName}}', t('assignmentPanel.validationTaskName'))
                    .replace('{{validationChipType}}', t('assignmentPanel.validationChipType'))
                    .replace('{{validationEstimatedAmount}}', t('assignmentPanel.validationEstimatedAmount'))
                    .replace('{{validationTrappingDate}}', t('assignmentPanel.validationTrappingDate'))
                    .replace('{{validationAONumber}}', t('assignmentPanel.validationAONumber'))
                    .replace('{{validationDrawMap}}', t('assignmentPanel.validationDrawMap'))
                    .replace('{{validationTaskGPS}}', t('assignmentPanel.validationTaskGPS'))
                    .replace('{{validationMinWorkingArea}}', t('assignmentPanel.validationMinWorkingArea'));
            });
            let validationMessage = `<div>${t('assignmentPanel.validationMissingFields')}</div><ul>${translatedFields.join("")}</ul>`;
            this.setState({ showValidationBox: true, validationMessage: validationMessage });
        } else {
            this.setState({ showValidationBox: false, validationMessage: "" });
        }

        return validation.isValid;
    }

    // Region is only editable by DL/Admin/Finance, so only block saving for those roles when it's missing
    private validateRegionSelected(): boolean {
        if (!(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance"))) {
            return true;
        }

        if (this.state.currentItem.regionID) {
            this.setState({ showValidationBox: false, validationMessage: "" });
            return true;
        }

        const { t } = this.props;
        let validationMessage = `<div>${t('assignmentPanel.validationMissingFields')}</div><ul><li>${t('assignmentPanel.regionsNummerLabel')}</li></ul>`;
        this.setState({ showValidationBox: true, validationMessage: validationMessage });
        return false;
    }

    private getVolumeDescription(): string {
        const { t } = this.props;
        let text = `${t('assignmentPanel.mapSumsTo')}: ${this.state.stackSumFromMap} rm`;
        if (this.state.stackTreeSpecices != "") {
            text = `${text} (${this.state.stackTreeSpecices})`;
        }
        return text;
    }

    private getTreeSpecicesUsedInStacks(mapData: IMapData): string {
        let all = mapData.stacks.filter(d => d.treeSpecices).map((v, i, a) => { return v.treeSpecices; });
        let treeSpecices = all.filter((item, index) => all.indexOf(item) === index);
        return treeSpecices.join(", ").toLowerCase();
    }

    private findSupplierById(supplierId?: string): ICreateOrUpdateSupplierDTO | undefined {
        if (!supplierId) return undefined;
        return this.state.suppliers.find(d => d.id == supplierId);
    }

    private getAssignmentForCrud(currentItem: IAssignmentDTO): IAssignmentDTO {
        return this.assignmentCrudHandler.prepareAssignmentForCrud(currentItem, true);
    }

    private async updateProperty(entity: Partial<IAssignmentDTO>): Promise<void> {
        this.setState(
            { currentItem: { ...this.state.currentItem, ...entity } },
            () => this.markUnsaved()
        );
    }

    private trimOrderName(newValue: string): string {
        return newValue.replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, '');
    }

    private filterSuggestedTags(filterText: string, selectedItems?: ITag[]): ITag[] {
        const res = filterText
            ? this.state.suppliers.filter(tag => tag.name.toLowerCase().indexOf(filterText.toLowerCase()) > -1)
            : this.state.suppliers;

        return res.map((v, i, a) => { return { key: v.id, name: v.name } });
    }

    private async onSupplierChange(items: ITag[] | undefined) {
        if (items && items.length > 0) {
            let supplierId = items[0].key.toString();
            let supplier = this.state.suppliers.find(d => d.id == supplierId);
            let projectNo = supplier?.projectNo;

            this.updateProperty({
                supplierID: supplierId,
                supplierName: supplier?.name,
                projectNo: projectNo,
            });

            // Load certifications for the selected supplier if needed
            if (supplierId) {
                let certifications = await this.userService.GetCertificationsForSupplier(supplierId);
                this.setState({ certifications: certifications });
            }
        }
    }

    private hasMapBeenChangedAfterDocumentationProcess() {
        let ci = this.state.currentItem;
        let result = false;

        // If both are undefined, not processed yet OR items before this new feature
        if (!ci.mapTimestamp && !ci.documentationTimestamp) {
            result = false;
        }

        // If maptimestamp is found and documentation timestamp is undefined, map has been changed since this new feature rolled out. Block!
        if (ci.mapTimestamp && !ci.documentationTimestamp) {
            result = true;
        }

        // If both maptimestamp and documentation timestamp are set, check if map has been changed after documentation. Block!
        if (ci.mapTimestamp && ci.documentationTimestamp) {
            result = ci.mapTimestamp.getTime() > ci.documentationTimestamp.getTime()
        }

        // If no files have been added, do not show warning yet
        if (!ci.files || ci.files.filter(d => d.type == "Documentation").length == 0) {
            result = false;
        }

        this.setState({ showDocumentationOutdatedWarning: result });
    }
    //#endregion

    //#region Assignment CRUD
    private async deleteAssignment() {
        const { t } = this.props;
        if (window.confirm(t('assignmentPanel.deleteConfirm'))) {
            let result: boolean = false;
            if (this.props.selectedAssignment) {
                result = await this.assignmentService.DeleteAssignment({ ID: this.state.currentItem.id + "" });
            }

            if (result) {
                this.props.onChanged(this.state.currentItem, false);
            } else {
                alert(t('assignmentPanel.deleteError'));
            }
        }
    }

    private async onAddOrUpdateAssignment(currentItem: IAssignmentDTO, keepPanelOpen: boolean = false) {
        const { t } = this.props;
        this.setState({ isSaving: true }, async () => {
            let result: boolean = false;
            const currentItemForCrud = this.getAssignmentForCrud({ ...currentItem });

            if (currentItemForCrud.id != undefined) {
                result = await this.assignmentService.UpdateAssignment(currentItemForCrud);
            } else {
                console.log("Creating new assignment with data:", currentItemForCrud);
                let newId = await this.assignmentService.CreateAssignment(currentItemForCrud);
                result = newId != "";
            }

            if (result) {
                this.props.onChanged(currentItem, keepPanelOpen);
            } else {
                let errorMessage = this.props.selectedAssignment ?
                    t('assignmentPanel.updateError') :
                    t('assignmentPanel.createError');
                alert(errorMessage);
            }

            this.setState({ isSaving: false });
        });
    }

    private async takeNextPINFONo() {
        let result = await this.assignmentService.TakeNextPINFONo(this.state.currentItem);
        this.setState({ currentItem: { ...this.state.currentItem, hdaoNo: result } });
    }

    private async addFiles(eventFiles: FileList, type: string) {
        let files = this.state.currentItem.files;

        let promises = Array.from(eventFiles).map(file => {
            let reader = new FileReader();
            return new Promise(resolve => {
                reader.readAsDataURL(file);
                reader.onload = () => {
                    files?.push({ name: file.name, content: reader.result + "", type: type, isNew: true });
                    resolve(reader.result);
                }
            });
        });

        await Promise.all(promises);
        this.updateProperty({ files: files });
    }

    private async deleteFile(file: IAssignmentFileDTO) {
        await this.assignmentService.DeleteFile(this.state.currentItem, file);
        let files = this.state.currentItem.files?.filter(d => d !== file);
        this.updateProperty({ files: files })
    }

    private async downloadFile(fileName: string) {
        let href = await this.assignmentService.DownloadFile(this.state.currentItem, fileName);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    //#endregion

    //#region Unsaved Changes Handling
    handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (this.shouldWarnOnLeave()) {
            e.preventDefault();
            e.returnValue = '';
            return '';
        }
    };

    handlePopState = (e: PopStateEvent) => {
        if (this.shouldWarnOnLeave()) {
            this.setState({ showLeaveConfirmDialog: true });
            window.history.pushState(null, '', window.location.href);
        }
    };

    shouldWarnOnLeave = () => {
        return (this.state.mode === FormMode.New || this.state.mode === FormMode.Edit) && this.hasUnsavedChanges;
    };

    markUnsaved = () => {
        if (!this.hasUnsavedChanges) {
            this.hasUnsavedChanges = true;
        }
    };

    clearUnsaved = () => {
        this.hasUnsavedChanges = false;
    };

    confirmLeave = () => {
        this.setState({ showLeaveConfirmDialog: false }, () => {
            this.clearUnsaved();
            window.removeEventListener('popstate', this.handlePopState);
            window.history.back();
        });
    };
    //#endregion

    //#region Render Action Buttons
    private renderActionButtons() {
        const { t } = this.props;

        return (<div style={{ marginTop: "10px" }}>
            {(this.state.mode == FormMode.New || this.state.mode == FormMode.Edit) &&
                <React.Fragment>
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")) && this.state.showValidationBox &&
                        <MessageBar className={styles.validationBox} messageBarType={MessageBarType.severeWarning} isMultiline={true}>
                            <div dangerouslySetInnerHTML={{ __html: this.state.validationMessage }} />
                        </MessageBar>
                    }
                    <VisibilityControl name="Gem kladde" visible={(App.IsUserInRole("Supplier") || App.IsUserInRole("Purchaser")) && (this.state.currentItem.status == Status.Open || this.state.currentItem.status == Status.RequiresAdditionalInfo) || ((App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")) && this.state.currentItem.status != Status.Approved && this.state.currentItem.status != Status.Rejected && this.state.currentItem.status != Status.RequiresAdditionalInfo)}>
                        <PrimaryButton
                            className={styles.button}
                            disabled={this.state.isSaving}
                            onClick={() => { if (this.validateRegionSelected()) this.onAddOrUpdateAssignment(this.state.currentItem) }}
                        >
                            {t('assignmentPanel.saveDraft')}
                        </PrimaryButton>
                    </VisibilityControl>
                    <VisibilityControl name="Overfør til Dalgas godkendelse" visible={App.IsUserInRole("Supplier") && (this.state.currentItem.status == Status.Open || this.state.currentItem.status == Status.RequiresAdditionalInfo) && !this.props.allowSelectionOfSupplier}>
                        <PrimaryButton
                            className={styles.button}
                            disabled={this.state.isSaving}
                            onClick={async () => {
                                let currentItem = { ...this.state.currentItem };
                                currentItem.status = Status.WaitingForApproval;
                                this.onAddOrUpdateAssignment(currentItem);
                            }}
                        >
                            {t('assignmentPanel.transferToApproval')}
                        </PrimaryButton>
                    </VisibilityControl>
                    <VisibilityControl name="Godkend" visible={(App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")) && (this.state.currentItem.status == Status.WaitingForApproval || this.state.currentItem.status == Status.RequiresAdditionalInfo || this.props.allowSelectionOfSupplier)}>
                        <PrimaryButton
                            className={styles.approveButton}
                            onClick={async () => {
                                if (this.validateForm_CanApprove()) {
                                    let currentItem = { ...this.state.currentItem };
                                    currentItem.status = "Godkendt";
                                    currentItem.modifiedBy = App.Username;
                                    this.onAddOrUpdateAssignment(currentItem);
                                }
                            }}
                            disabled={this.state.isSaving || this.state.showDocumentationOutdatedWarning}
                        >
                            {t('assignmentPanel.approve')}
                        </PrimaryButton>
                    </VisibilityControl>
                    <VisibilityControl name="Afvis" visible={App.IsUserInRole("DL") && (this.state.currentItem.status == Status.WaitingForApproval)}>
                        <PrimaryButton
                            className={styles.rejectButton}
                            disabled={this.state.isSaving}
                            onClick={async () => {
                                let currentItem = { ...this.state.currentItem };
                                currentItem.status = "Afvist";
                                this.onAddOrUpdateAssignment(currentItem);
                            }}
                        >
                            {t('assignmentPanel.reject')}
                        </PrimaryButton>
                    </VisibilityControl>
                </React.Fragment>
            }
            <VisibilityControl
                name="Slet opgave"
                visible={
                    (((App.IsUserInRole("Purchaser") || App.IsUserInRole("DL")) && (this.state.mode == FormMode.Edit || this.state.mode == FormMode.ReadOnly)) && (this.state.currentItem.status == Status.Open || this.state.currentItem.status == Status.Rejected || this.state.currentItem.status == Status.WaitingForApproval)) ||
                    (App.IsUserInRole("Supplier") && ((this.state.mode == FormMode.Edit && this.state.currentItem.status == Status.Open) || this.state.currentItem.status == Status.RequiresAdditionalInfo))
                }
            >
                <PrimaryButton
                    className={styles.deleteButton}
                    onClick={async () => await this.deleteAssignment()}
                >
                    {t('assignmentPanel.deleteTask')}
                </PrimaryButton>
            </VisibilityControl>
            <DefaultButton onClick={() => this.props.onDismiss()}>{t('common.cancel')}</DefaultButton>
        </div>);
    }
    //#endregion

    //#region Render Panel
    public render(): React.ReactElement<IGermanAssignmentPanelProps> {
        const { t } = this.props;

        return (<>
            <Panel
                headerText={this.props.selectedAssignment ? t('assignmentPanel.editTask') : t('assignmentPanel.createNewTask')}
                isOpen={true}
                className={styles.createPanel}
                isLightDismiss={false}
                isBlocking={true}
                onDismiss={() => { this.props.onDismiss(); }}
                closeButtonAriaLabel={t('assignmentPanel.closePanel')}
                type={PanelType.medium}
                onRenderFooterContent={!isMobileOrTablet() ? this.renderActionButtons.bind(this) : undefined}
                styles={
                    isMobileOrTablet() ? {
                        main: {
                            width: '75vw',
                            minWidth: '75vw',
                            maxWidth: '100vw',
                            height: '100%',
                            maxHeight: '100%',
                            overflowY: 'auto',
                        }
                    } : undefined
                }
            >
                <Stack style={isMobileOrTablet() ? { paddingBottom: 120 } : undefined}>
                    {isMobileOrTablet() && (
                        <div className={styles.stickyActionBar}>
                            {this.renderActionButtons()}
                        </div>
                    )}
                    {this.state.isOffline && (
                        <MessageBar messageBarType={MessageBarType.severeWarning}>
                            {t('dashboard.offlineMessage')}
                        </MessageBar>
                    )}
                    {this.state.mode != FormMode.New &&
                        <MessageBar messageBarType={this.state.currentItem.status == Status.Rejected ? MessageBarType.error : MessageBarType.info} isMultiline={false}>
                            <div>{t('assignmentPanel.taskType')}: {this.state.currentItem.typeId}</div>
                            {!App.IsUserInRole("Supplier") && <div>{t('assignmentPanel.supplier')}: {this.state.currentItem.supplierName}</div>}
                            <div>{t('assignmentPanel.taskCreatedBy')}: {this.state.currentItem.createdBy}</div>
                            <span>Status: {t(`status.${this.state.currentItem.status}`)}</span>
                            {this.state.currentItem.status == Status.Approved &&
                                <span>&nbsp;{this.state.currentItem.approvedDate?.toLocaleDateString()} af {this.state.currentItem.modifiedBy}</span>
                            }
                        </MessageBar>
                    }
                    {(this.props.allowSelectionOfSupplier || (this.state.currentItem.status === Status.Offline)) &&
                        <>
                            <Label required>{t('assignmentPanel.supplier')}</Label>
                            <TagPicker
                                itemLimit={1}
                                selectionAriaLabel={t('assignmentPanel.selectSupplier')}
                                onResolveSuggestions={this.filterSuggestedTags.bind(this)}
                                getTextFromItem={(item: ITag) => item.name}
                                pickerSuggestionsProps={{
                                    suggestionsHeaderText: t('assignmentPanel.suppliers'),
                                    noResultsFoundText: t('assignmentPanel.noMatchFound'),
                                }}
                                inputProps={{
                                    placeholder: t('userManagement.searchSupplier')
                                }}
                                disabled={this.state.mode === FormMode.ReadOnly || this.state.isOffline}
                                onChange={this.onSupplierChange.bind(this)}
                                defaultSelectedItems={
                                    this.state.currentItem.supplierID && this.state.currentItem.supplierName
                                        ? [{ key: this.state.currentItem.supplierID, name: this.state.currentItem.supplierName }]
                                        : undefined
                                }
                            />
                        </>
                    }
                    <TextField
                        required={true}
                        onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.taskName.name')} description={t('tooltip.assignmentPanel.taskName.description')} /> }}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        placeholder={t('assignmentPanel.taskNamePlaceholder')}
                        value={this.state.currentItem.orderName}
                        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                            this.updateProperty({ orderName: this.trimOrderName(newValue + "") })
                        }}
                    />

                    {(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance") || App.IsUserInRole("Purchaser")) &&
                        <React.Fragment>
                            <Stack horizontal horizontalAlign="space-between" verticalAlign="end">
                                <Stack.Item grow={8}>
                                    <TextField
                                        value={this.state.currentItem.hdaoNo != undefined ? this.state.currentItem.hdaoNo.toLocaleString() : undefined}
                                        label={t('assignmentPanel.dalgasAOLabel')}
                                        required={true}
                                        disabled={true}
                                        multiline={false}
                                    />
                                </Stack.Item>
                                <Stack.Item grow={1}>
                                    <IconButton
                                        disabled={this.state.currentItem.hdaoNo != undefined || this.state.isOffline}
                                        iconProps={{ iconName: "PageLock" }}
                                        title={t('assignmentPanel.takeNextAO')}
                                        ariaLabel={t('assignmentPanel.takeNextAO')}
                                        onClick={this.takeNextPINFONo.bind(this)}
                                    />
                                </Stack.Item>
                            </Stack>
                            <TextField
                                value={this.state.currentItem.projectNo}
                                label={t('assignmentPanel.projectNumberLabel')}
                                required={false}
                                disabled={this.state.mode == FormMode.ReadOnly || this.state.isOffline}
                                multiline={false}
                                onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                    if (newValue && newValue?.length <= 20) this.updateProperty({ projectNo: newValue })
                                })}
                            />
                        </React.Fragment>
                    }

                    {App.IsUserInRole("Supplier") &&
                        <TextField
                            onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.dalgasWorkOrder.name')} description={t('tooltip.assignmentPanel.dalgasWorkOrder.description')} /> }}
                            value={this.state.currentItem.hdaoNo != undefined ? this.state.currentItem.hdaoNo.toLocaleString() : undefined}
                            disabled={true}
                            multiline={false}
                        />
                    }

                    <Dropdown
                        required={true}
                        placeholder={t('common.selectFromList')}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.chipType.name')} description={t('tooltip.assignmentPanel.chipType.description')} /> }}
                        selectedKey={this.state.currentItem.biomassCategory}
                        options={[
                            // { key: 'Skov: rundtræ/stammetræ', text: 'Skov: rundtræ/stammetræ' },
                            { key: 'Skov: restprodukter fra skovbrug inkl. alle tyndinger', text: 'Wald: Restmaterial und Durchf' },
                            { key: 'Skov: afdrift kun til energi (afdrift hvor alt materiale går til flis)', text: 'Wald - Kahlschlagg mit nur Hacks' },
                            { key: 'Ikke skov: læhegn, småbevoksninger under 0,5ha og naturpleje', text: 'Landschaftspflege' },
                            { key: 'Træaffald inkl. fra omkring infrastruktur, byggemodning, have-, park-,  vej- og bytræer', text: 'Infrastruktur und Räumung' },
                            // { key: 'Landbrug: Energipil og energipoppel', text: 'Landbrug: Energipil og energipoppel' },
                            // { key: 'Resttræ fra landbrugsarealer inkl juletræer og frugtplantager', text: 'Resttræ fra landbrugsarealer inkl juletræer og frugtplantager' },
                            // { key: 'Træindustri: restprodukter fra træindustri', text: 'Træindustri: restprodukter fra træindustri' },
                            { key: 'Rundtræ', text: 'Rundholz' }
                        ]}
                        onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
                            this.updateProperty({ biomassCategory: option?.key + "" });
                        }}
                    />

                    <TextField
                        multiline={true}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        rows={6}
                        onRenderLabel={() => {
                            return <TooltipComponent name={t('tooltip.assignmentPanel.remarks.name')} description={t('tooltip.assignmentPanel.remarks.description')} />
                        }}
                        placeholder={t('assignmentPanel.additionalInfoPlaceholder')}
                        value={this.state.currentItem.description}
                        onChange={(event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                            this.updateProperty({ description: newValue })
                        }}
                    />

                    <TooltipComponent name={t('tooltip.assignmentPanel.fellingDate.name')} description={t('tooltip.assignmentPanel.fellingDate.description')} />
                    <DatePicker
                        firstDayOfWeek={DayOfWeek.Monday}
                        placeholder={t('assignmentPanel.trappingDatePlaceholder')}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        isRequired={App.IsUserInRole("DL") || App.IsUserInRole("Purchaser")}
                        value={
                            App.IsUserInRole("Purchaser")
                                ? this.state.currentItem.trappingDate || new Date(new Date().setDate(new Date().getDate() - 14))
                                : this.state.currentItem.trappingDate || undefined
                        }
                        formatDate={Utilities.FormatDate}
                        onSelectDate={(val) => {
                            this.updateProperty({ trappingDate: val != null ? val : undefined });
                        }}
                        strings={Utilities.DayPickerStrings}
                    />

                    <PrimaryButton
                        className={styles.defaultButton}
                        onClick={() => { this.setState({ step: CreationSteps.Map }); }}
                        disabled={this.state.isOffline}
                    >
                        <TooltipComponent
                            name={(this.state.currentItem.status == Status.Approved || this.state.currentItem.status == Status.Rejected || this.state.currentItem.status == Status.WaitingForApproval || this.state.currentItem.status == Status.Completed) ? t('tooltip.assignmentPanel.openMap.name') : t('tooltip.assignmentPanel.drawMap.name')}
                            description={t('tooltip.assignmentPanel.drawMap.description')}
                        />
                    </PrimaryButton>

                    {this.state.step == CreationSteps.Map &&
                        <GermanLeafletMap
                            currentItem={this.state.currentItem}
                            germanBackgroundLayers={this.germanBackgroundLayers}
                            germanOverlayLayers={this.germanOverlayLayers}
                            onDismiss={(currentItem: IAssignmentDTO) => { this.setState({ step: CreationSteps.Assignment }); }}
                            onSaveClicked={(mapData: IMapData, canEdit: boolean, mapRef?: any, layers?: any[], wfsFeatures?: { [layerName: string]: any }) => {
                                this.onMapSaved(mapData, canEdit, mapRef, layers, wfsFeatures);
                            }}
                            onSaveDueToInactivity={async (mapData: IMapData) => {
                                this.onMapSaved(mapData, true);
                                this.onAddOrUpdateAssignment(this.state.currentItem, true);
                                this.setState({ assignmentSavedAfterInactivity: true });
                            }}
                        />
                    }

                    {(this.state.mode == FormMode.New || this.state.mode == FormMode.Edit) &&
                        <React.Fragment>
                            <PrimaryButton
                                className={styles.defaultButton}
                                onClick={() => { this.fileInput_OwnMap.click() }}
                                disabled={this.state.isOffline}
                            >
                                <TooltipComponent
                                    name={t('tooltip.assignmentPanel.uploadOwnMap.name')}
                                    description={t('tooltip.assignmentPanel.uploadOwnMap.description')}
                                />
                            </PrimaryButton>
                            <input
                                style={{ display: "none" }}
                                type="file"
                                multiple={true}
                                ref={fileInput => this.fileInput_OwnMap = fileInput}
                                onChange={async (event) => {
                                    if (event.target.files) {
                                        await this.addFiles(event.target.files, "OwnMap");
                                    }
                                }}
                                disabled={this.state.isOffline}
                            />
                        </React.Fragment>
                    }
                    {this.state.mode != FormMode.ReadOnly &&
                        <PrimaryButton className={styles.defaultButton} onClick={async () => { if (this.state.currentItem.id == undefined) await this.preSaveAssignment(); this.setState({ step: CreationSteps.Documentation }); }} disabled={(App.IsUserInRole("DL") && !this.state.currentItem.supplierID) || this.state.currentItem.mapData == undefined || (JSON.parse(this.state.currentItem.mapData) as IMapData).fixedTaskPosition == undefined}>
                            <TooltipComponent name={t('tooltip.assignmentPanel.createDocumentation.name')} description={t('tooltip.assignmentPanel.createDocumentation.description')} />
                        </PrimaryButton>
                    }
                    {this.state.step == CreationSteps.Documentation &&
                        <GermanDocumentationDialog
                            currentItem={this.state.currentItem}
                            mapRef={this.state.mapRef}
                            backgroundLayers={this.germanBackgroundLayers}
                            layers={this.germanOverlayLayers}
                            wfsFeatures={this.state.wfsFeatures}
                            t={t}
                            onDismiss={() => { this.setState({ step: CreationSteps.Assignment }); }}
                            onSaved={(newFiles: IAssignmentFileDTO[]) => {
                                let currentItem = this.state.currentItem;

                                let allFiles = currentItem.files?.filter(d => d.type != "Documentation");
                                allFiles?.push(...newFiles);

                                this.setState({ currentItem: { ...this.state.currentItem, files: allFiles, documentationTimestamp: new Date() }, step: CreationSteps.Assignment }, () => {
                                    this.hasMapBeenChangedAfterDocumentationProcess();
                                });
                            }}
                        />
                    }

                    <Label>{t('assignmentPanel.documentation')}</Label>
                    <div>
                        {this.state.currentItem.files?.filter(d => d.type == "OwnMap")?.map((v, i, a) => {
                            return <div key={`documentationFile_${i}`}>
                                {v.isNew ?
                                    <Label className={styles.newLink}>{v.name}</Label> :
                                    <div>
                                        <Link onClick={async () => { await this.downloadFile(v.name); }}>
                                            {v.name}
                                        </Link>
                                        {v.type == "OwnMap" &&
                                            <IconButton
                                                onClick={() => {
                                                    if (window.confirm(t('assignmentPanel.deleteConfirm')))
                                                        this.deleteFile(v);
                                                }}
                                                iconProps={{ iconName: 'Delete' }}
                                                title={t('assignmentPanel.delete')}
                                            />
                                        }
                                    </div>
                                }
                            </div>
                        })}
                        {this.state.mode != FormMode.New && this.state.currentItem.files?.filter(d => d.type == "OwnMap").length == 0 &&
                            <div>{t('assignmentPanel.noOwnMapUploaded')}</div>
                        }
                    </div>
                    <div>
                        {this.state.currentItem.files?.filter(d => d.type == "OwnMap" || d.type == "Documentation")?.map((v, i, a) => {
                            return (
                                <div key={`documentationFile_${i}`}>
                                    {v.isNew ?
                                        <Label className={styles.newLink}>
                                            {v.name}
                                        </Label>
                                        :
                                        <div>
                                            <Link onClick={async () => { this.downloadFile(v.name); }}>
                                                {v.name} ({v.type == "OwnMap" ? t('assignmentPanel.ownMap') : t('assignmentPanel.dalgasDocumentation')})
                                            </Link>
                                            {v.type == "OwnMap" &&
                                                <IconButton
                                                    onClick={() => {
                                                        if (window.confirm(t('assignmentPanel.deleteFileConfirm')) == true)
                                                            this.deleteFile(v);
                                                    }}
                                                    iconProps={{ iconName: 'Delete' }} title={t('assignmentPanel.delete')}
                                                />
                                            }
                                        </div>
                                    }
                                </div>
                            );
                        })}
                        {App.IsUserInRole("DL") && this.state.currentItem.files?.filter(d => d.type == "OwnMap").length == 1 &&
                            <React.Fragment>
                                <Label>{t('assignmentPanel.ownMapApprovedLabel')}</Label>
                                <div className={styles.toggleOptions}>
                                    <Label className={styles.toggleLabelLeft}>{t('common.no')}</Label>
                                    <Toggle
                                        disabled={this.state.mode == FormMode.ReadOnly}
                                        checked={this.state.currentItem.isOwnMapApproved}
                                        onChange={(ev, checked?: boolean) => { this.updateProperty({ isOwnMapApproved: checked }) }}
                                    />
                                    <Label className={styles.toggleLabelRight}>{t('common.yes')}</Label>
                                </div>
                            </React.Fragment>
                        }
                    </div>
                    {this.state.showDocumentationOutdatedWarning &&
                        <div className={styles.documentationOutdated}>
                            <Icon iconName='Important' />
                            <span>{t('assignmentPanel.documentationOutdated')}</span>
                        </div>
                    }

                    <TextField
                        value={this.state.currentItem.estimatedAmount ? this.state.currentItem.estimatedAmount + "" : undefined}
                        onRenderLabel={() => { return <TooltipComponent name={t('tooltip.assignmentPanel.estimatedAmount.name')} description={t('tooltip.assignmentPanel.estimatedAmount.description')} /> }}
                        description={this.getVolumeDescription()}
                        placeholder={t('assignmentPanel.estimatedAmountPlaceholder')}
                        disabled={this.state.mode == FormMode.ReadOnly}
                        suffix={t('common.rm')}
                        required={true}
                        onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                            const newAmount = parseFloat(newValue + "");
                            const shouldOverride = this.state.stackSumFromMap > 0;
                            this.updateProperty({ estimatedAmount: newAmount });
                            if (shouldOverride) {
                                this.setState({ userOverrideEstimatedAmount: true });
                            }
                        })}
                    />

                    {(this.state.currentItem.status == Status.RequiresAdditionalInfo || this.state.currentItem.status == Status.Rejected || App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance")) &&
                        <TextField
                            value={this.state.currentItem.comment}
                            label={t('assignmentPanel.dalgasCommentsLabel')}
                            multiline={true}
                            rows={6}
                            disabled={!App.IsUserInRole("DL") || this.state.mode == FormMode.ReadOnly}
                            onChange={((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                this.updateProperty({ comment: newValue })
                            })}
                        />
                    }

                    {(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance")) &&
                        <Dropdown
                            required={true}
                            placeholder={t('common.selectFromList')}
                            disabled={this.state.mode == FormMode.ReadOnly}
                            label={t('assignmentPanel.resourceTypeLabel')}
                            selectedKey={this.state.currentItem.ressourceType}
                            options={[
                                { key: 'Flis jord', text: 'Hackschnitzelpolter (gehackt)' },
                                { key: 'Grot', text: 'Kronenrestholz-Nadelholz' },
                                { key: 'Fældeudkørt', text: 'Ganzbäume gerückt' },
                                { key: 'Skovlager', text: 'Waldlager (ungehackt)' }
                            ]}
                            onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
                                this.updateProperty({ ressourceType: option?.key.toString() })
                            }}
                        />
                    }
                    {(App.IsUserInRole("DL") || App.IsUserInRole("Admin") || App.IsUserInRole("Finance")) && (this.state.mode != FormMode.ReadOnly) &&
                        <>
                            <Dropdown
                                required={true}
                                placeholder={t('common.selectFromList')}
                                label={t('assignmentPanel.regionsNummerLabel')}
                                selectedKey={this.state.currentItem.regionID}
                                options={this.state.regions.map(region => ({ key: region.id, text: region.name }))}
                                onChange={(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption, index?: number) => {
                                    let selectedRegion = this.state.regions.find(region => region.id === option?.key);
                                    this.updateProperty({ regionID: selectedRegion?.id, regionName: selectedRegion?.name })
                                }}
                            />
                            <Text>
                                {t('assignmentPanel.selectRegionNumberForPinfo')}
                            </Text>
                        </>
                    }
                </Stack>
            </Panel>

            {this.state.assignmentSavedAfterInactivity &&
                <Dialog hidden={false} minWidth={250}>
                    <div dangerouslySetInnerHTML={{ __html: t('assignmentPanel.autoSaveMessage') }} />
                    <DialogFooter>
                        <PrimaryButton
                            text={t('common.understood')}
                            onClick={() => { this.setState({ assignmentSavedAfterInactivity: false }); }}
                        />
                    </DialogFooter>
                </Dialog>
            }

            {this.state.showLeaveConfirmDialog && (
                <div className="leave-confirm-dialog" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.4)', zIndex: 9999 }}>
                    <div style={{ background: 'white', padding: 32, borderRadius: 8, maxWidth: 400, margin: '10vh auto', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <h3>{t('assignmentPanel.confirmLeaveTitle')}</h3>
                        <p>{t('assignmentPanel.confirmLeaveMessage')}</p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
                            <button onClick={() => this.setState({ showLeaveConfirmDialog: false })}>{t('assignmentPanel.stayOnPage')}</button>
                            <button onClick={this.confirmLeave}>{t('assignmentPanel.leaveAndDiscard')}</button>
                        </div>
                    </div>
                </div>
            )}
        </>);
    }
    //#endregion
}

export default withLanguage(GermanAssignmentPanel);
